/**
 * Setup (plans/workbench2, plan §0.6): the workbench's own surface for a
 * case set up from a request — the request and its files with the entries
 * the model wrote; the subjects, confirmed from the case agent's proposal
 * or typed; the report type from the playbook's codes; the checklist;
 * partner work, approved into an assignment with the partner and the
 * terms; the price; what Start still needs; and Start. Underneath, triage
 * is mechanism: each action calls today's service, and Start walks the
 * stages that remain. Nothing here is a stepper.
 */

import { Accordion, Alert, Badge, Group, List, Loader, Spoiler, Stack, Text } from '@mantine/core';

import type { WorkbenchHeader, WorkbenchSetup } from '@/types/workbench';
import { ChecklistPanel } from '../ChecklistPanel';
import { FilesView } from '../FilesView';
import { StartButton } from '../StartButton';
import { SetupActionsProvider } from './setupActions';
import { SetupPartners } from './SetupPartners';
import { SetupPrice, SetupReportType } from './SetupParts';
import { SetupSubjects } from './SetupSubjects';

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Each part's line, so the setup reads at a glance. */
function summaries(setup: WorkbenchSetup): Record<string, string> {
  const open = setup.partners.asks.filter((a) => a.status === 'open').length;
  const unnamed = setup.subjects.filter((s) => s.unnamed).length;
  return {
    request: `${plural(setup.files.length, 'file')}${
      setup.files.filter((f) => f.key !== null).length < setup.files.length
        ? ', some not entered yet'
        : ''
    }`,
    subjects: setup.subjectsConfirmed
      ? `${plural(setup.subjects.length, 'subject')}${unnamed ? ` · ${unnamed} not named yet` : ''}`
      : setup.proposals.some((p) => p.kind === 'subjects')
        ? 'Proposed by the case agent, to confirm'
        : 'Not confirmed yet',
    report_type: setup.reportType.code
      ? `${setup.reportType.code}${setup.reportType.name ? ` · ${setup.reportType.name}` : ''}`
      : 'Not chosen',
    checklist: setup.checklist.length > 0 ? plural(setup.checklist.length, 'check') : 'Nothing yet',
    partners:
      (setup.partners.asks.length > 0
        ? `${plural(setup.partners.asks.length, 'ask')}${open ? `, ${open} waiting for you` : ''} · ${plural(setup.partners.assignments.length, 'assignment')}`
        : `None asked for · ${plural(setup.partners.assignments.length, 'assignment')}`) +
      (setup.partners.prefill ? ' · form pre-filled' : ''),
    price: setup.price.price
      ? `Set${setup.price.turnaroundDays != null ? ` · ${setup.price.turnaroundDays} days` : ''}`
      : setup.price.suggested
        ? 'Not set · suggested'
        : 'Not set',
  };
}

const PARTS: readonly [string, string][] = [
  ['request', 'The request and its files'],
  ['subjects', 'Subjects'],
  ['report_type', 'Report type'],
  ['checklist', 'Checklist'],
  ['partners', 'Partner work'],
  ['price', 'Price'],
];

function Request({ setup }: Readonly<{ setup: WorkbenchSetup }>) {
  const { request } = setup;
  return (
    <Stack gap="sm">
      {request ? (
        <Stack gap={2}>
          {request.subjectLine ? (
            <Text size="sm" fw={600}>
              {request.subjectLine}
            </Text>
          ) : null}
          {request.text ? (
            <Spoiler maxHeight={84} showLabel="Show the whole request" hideLabel="Show less">
              <Text size="xs" style={{ whiteSpace: 'pre-line' }} data-testid="setup-request-text">
                {request.text}
                {request.truncated ? ' …' : ''}
              </Text>
            </Spoiler>
          ) : (
            <Text size="xs" c="dimmed">
              The request has no text of its own.
            </Text>
          )}
        </Stack>
      ) : (
        <Text size="xs" c="dimmed">
          No request on record.
        </Text>
      )}
      <FilesView reportId={setup.reportId} files={setup.files} error={null} />
    </Stack>
  );
}

function Part({
  part,
  setup,
  header,
  onChanged,
}: Readonly<{
  part: string;
  setup: WorkbenchSetup;
  header: WorkbenchHeader;
  onChanged: () => Promise<void>;
}>) {
  switch (part) {
    case 'request':
      return <Request setup={setup} />;
    case 'subjects':
      return <SetupSubjects setup={setup} />;
    case 'report_type':
      return <SetupReportType setup={setup} />;
    case 'checklist':
      return (
        <ChecklistPanel
          reportId={setup.reportId}
          items={setup.checklist}
          subjects={header.subjects}
          editable={setup.editable}
          onChanged={onChanged}
        />
      );
    case 'partners':
      return <SetupPartners setup={setup} />;
    default:
      return <SetupPrice setup={setup} />;
  }
}

interface SetupViewProps {
  setup: WorkbenchSetup;
  header: WorkbenchHeader;
  /** The case moved: read the setup and the case again. */
  onChanged: () => Promise<void>;
}

export function SetupView({ setup, header, onChanged }: Readonly<SetupViewProps>) {
  const lines = summaries(setup);
  const { start } = setup;
  const blocking = start.needs.filter((n) => n.blocking);
  const notices = start.needs.filter((n) => !n.blocking);
  return (
    <SetupActionsProvider
      reportId={setup.reportId}
      editable={setup.editable}
      partnerWorkEditable={setup.partnerWorkEditable}
      commercialsEditable={setup.mode === 'live'}
      started={setup.started}
      evaluationCopy={setup.evaluationCopy}
      onChanged={onChanged}
    >
      <Stack gap="sm" data-testid="setup-view">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
          <Stack gap={2}>
            <Group gap={6}>
              <Text size="sm" fw={600}>
                {start.started ? 'Started' : 'Setting the case up'}
              </Text>
              <Badge size="xs" variant="light" color="gray" style={{ textTransform: 'none' }}>
                {setup.stage.replace(/_/g, ' ')}
              </Badge>
              {!setup.editable && !start.started ? (
                <Badge size="xs" variant="light" color="orange" style={{ textTransform: 'none' }}>
                  read-only
                </Badge>
              ) : null}
            </Group>
            {!start.started ? (
              <Text size="xs" c="dimmed">
                Research begins on arrival; Start is your commitment — partner work is dispatched
                and the case runs under its case budget.
                {start.remaining.length > 0
                  ? ` Start walks ${start.remaining.map((s) => s.replace(/_/g, ' ')).join(', ')}.`
                  : ''}
              </Text>
            ) : null}
            {header.run.status ? (
              <Group gap={6} wrap="nowrap" data-testid="setup-run">
                <Loader size={12} color="blue" />
                <Text size="xs" c="blue.8">
                  The case agent is {header.run.status === 'queued' ? 'queued to work' : 'working'}{' '}
                  on the request; its proposals appear here when it finishes.
                </Text>
              </Group>
            ) : null}
          </Stack>
          {!start.started && setup.editable ? (
            <StartButton
              reportId={setup.reportId}
              needs={blocking.map((n) => n.label)}
              canStart={start.canStart}
              onStarted={onChanged}
            />
          ) : null}
        </Group>
        {!start.started && (blocking.length > 0 || notices.length > 0) ? (
          <Alert
            color={blocking.length > 0 ? 'orange' : 'blue'}
            variant="light"
            data-testid="setup-needs"
          >
            <Stack gap={2}>
              {blocking.length > 0 ? (
                <>
                  <Text size="sm" fw={600}>
                    Start still needs
                  </Text>
                  <List size="sm">
                    {blocking.map((need) => (
                      <List.Item key={need.code} data-testid={`setup-need-${need.code}`}>
                        {need.label}
                      </List.Item>
                    ))}
                  </List>
                </>
              ) : null}
              {notices.length > 0 ? (
                <Text size="xs" c="dimmed">
                  Also: {notices.map((n) => n.label).join('; ')}.
                </Text>
              ) : null}
            </Stack>
          </Alert>
        ) : null}
        <Accordion
          multiple
          variant="separated"
          chevronPosition="left"
          defaultValue={['subjects', 'report_type', 'partners', 'price']}
        >
          {PARTS.map(([part, label]) => (
            <Accordion.Item key={part} value={part} data-testid={`setup-part-${part}`}>
              <Accordion.Control>
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm" fw={500}>
                    {label}
                  </Text>
                  <Text size="xs" c="dimmed" truncate>
                    {lines[part]}
                  </Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Part part={part} setup={setup} header={header} onChanged={onChanged} />
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      </Stack>
    </SetupActionsProvider>
  );
}
